import { useCallback, useRef } from 'react';
import { useModal } from '@/hooks/useModal';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useAuthStore } from '@/stores/authStore';
import { getExistingAccountsWithPaymentMethod } from '@/lib/api/user';
import { maskEmail } from '../logic/duplicatePayment.logic';
import {
    trackGoToLoginFromDuplicatePayment,
    trackPaymentMethodHasOtherAccounts,
} from '../logic/subscriptionAnalytics.logic';

interface UserMeta {
    user_id?: string | number;
    username?: string;
    email?: string;
    full_name?: string;
}

interface UseDuplicatePaymentCheckProps {
    userMeta: UserMeta;
    hasPastSubscription: boolean;
    onConfirm: (paymentMethodId: string) => Promise<void>;
}

/**
 * Hook for handling duplicate payment method detection
 * Shows a modal when a payment method is already used by another account
 */
export const useDuplicatePaymentCheck = ({
    userMeta,
    hasPastSubscription,
    onConfirm,
}: UseDuplicatePaymentCheckProps) => {
    const { push } = useStanRouter();
    const logout = useAuthStore(state => state.logout);
    const modal = useModal();

    // Store data for modal display and confirmation
    const pendingPaymentMethodIdRef = useRef<string>('');
    const maskedEmailRef = useRef<string>('');

    /**
     * Check if payment method is already used by another account
     * Only for new sign ups (not users with subscription history)
     * Returns true if duplicates found and modal was shown
     */
    const checkForDuplicates = useCallback(
        async (paymentMethodId: string): Promise<boolean> => {
            // Only check for new signups, not returning users
            if (hasPastSubscription) {
                return false;
            }

            try {
                const { other_emails } = await getExistingAccountsWithPaymentMethod(paymentMethodId);
                const [firstEmail] = other_emails;

                if (firstEmail) {
                    // Track analytics
                    trackPaymentMethodHasOtherAccounts(userMeta, other_emails);

                    // Store data for modal
                    pendingPaymentMethodIdRef.current = paymentMethodId;
                    maskedEmailRef.current = maskEmail(firstEmail);

                    // Show modal
                    modal.open();
                    return true;
                }
            } catch (error) {
                // If the check fails, continue with subscription creation
                // This is non-blocking - we don't want to prevent subscription if this API fails
                console.error('Failed to check for duplicate payment method:', error);
            }

            return false;
        },
        [hasPastSubscription, userMeta, modal]
    );

    /**
     * Handle user confirming they want to continue with duplicate payment method
     */
    const handleConfirm = useCallback(async () => {
        modal.close();
        await onConfirm(pendingPaymentMethodIdRef.current);
    }, [modal, onConfirm]);

    /**
     * Handle user choosing to go to login instead
     */
    const handleGoToLogin = useCallback(() => {
        trackGoToLoginFromDuplicatePayment(userMeta);
        modal.close();
        logout();
        push('/login');
    }, [userMeta, modal, logout, push]);

    return {
        isModalOpen: modal.isOpen,
        maskedEmail: maskedEmailRef.current,
        checkForDuplicates,
        handleConfirm,
        handleGoToLogin,
    };
};
